import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ListingCard, formatPrice } from "@/components/ListingCard";
import { Stethoscope, Building2, Home as HomeIcon, HeartPulse, Shield, Award, TrendingUp, Users, Phone } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1706808849827-7366c098b317?crop=entropy&cs=srgb&fm=jpg&q=85";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [sold, setSold] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "", subject: "General Inquiry" });
  const [contactMsg, setContactMsg] = useState("");

  useEffect(() => {
    api.get("/public/listings", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/public/listings/sold", { params: { limit: 4 } }).then((r) => setSold(r.data)).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/contact", contact);
      setContactMsg("Thank you — a senior advisor will be in touch within 24 hours.");
      setContact({ name: "", email: "", phone: "", message: "", subject: "General Inquiry" });
    } catch { setContactMsg("Please try again."); }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/70 via-navy/60 to-navy" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl animate-fade-up">
            <div className="overline text-gold mb-6">Est. Excellence · Global Reach</div>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Elevating <span className="text-gold italic">Real Estate</span> Standards
            </h1>
            <p className="mt-8 text-lg text-white/75 max-w-xl leading-relaxed">
              Specializing in homes, commercial properties, medical clinics, and existing dental practice sales.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/listings" className="btn-gold" data-testid="hero-view-listings-btn">View Listings</Link>
              <Link to="/residential" className="btn-ghost-gold" data-testid="hero-sell-btn">Sell Your Property</Link>
              <Link to="/appointment" className="btn-ghost-gold" data-testid="hero-book-btn">Book Consultation</Link>
            </div>
            <a
              href="tel:+14033366663"
              className="mt-8 inline-flex items-center gap-3 group"
              data-testid="hero-phone"
            >
              <span className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center group-hover:bg-gold/10 transition">
                <Phone className="w-4 h-4 text-gold" strokeWidth={2} />
              </span>
              <span className="leading-tight">
                <span className="block text-[10px] tracking-[0.25em] uppercase text-gold">Speak with an Advisor</span>
                <span className="block font-display text-2xl text-white group-hover:text-gold transition">+1 403-336-6663</span>
              </span>
            </a>
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { n: "$2.4B+", l: "Transacted" },
              { n: "800+", l: "Deals Closed" },
              { n: "37", l: "Healthcare Markets" },
              { n: "96%", l: "Close Rate" },
            ].map((s) => (
              <div key={s.l} className="bg-navy-dark/90 p-6 text-center">
                <div className="font-display text-3xl text-gold">{s.n}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED LISTINGS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-gold">Featured Listings</div>
            <h2 className="font-display text-3xl lg:text-4xl text-navy mt-3">A curated selection</h2>
          </div>
          <Link to="/listings" className="hidden md:inline-flex text-sm text-navy hover:text-gold font-semibold" data-testid="all-listings-link">All Listings →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((l) => <ListingCard key={l.id} l={l} />)}
        </div>
      </section>

      {/* WHY FORTE */}
      <section className="bg-navy text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline text-gold">Why Forte Realty</div>
          <h2 className="font-display text-3xl lg:text-4xl mt-3 max-w-2xl">Discretion, expertise, and results — in that order.</h2>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { i: Shield, t: "Absolute Confidentiality", d: "NDA-backed process for healthcare and sensitive transactions." },
              { i: Award, t: "Credentialed Advisors", d: "Brokers with institutional and healthcare-specific expertise." },
              { i: TrendingUp, t: "Market Intelligence", d: "Proprietary data on prime residential and commercial markets." },
              { i: Users, t: "White-Glove Service", d: "One senior advisor owns your transaction end-to-end." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="bg-navy p-8">
                <Icon className="w-8 h-8 text-gold mb-6" strokeWidth={1.5} />
                <div className="font-display text-xl mb-3">{t}</div>
                <div className="text-sm text-white/70 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="relative overflow-hidden bg-navy min-h-[420px]">
            <img src="https://images.pexels.com/photos/4269276/pexels-photo-4269276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Dental practice" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="relative p-10 lg:p-14 h-full flex flex-col">
              <Stethoscope className="w-8 h-8 text-gold" strokeWidth={1.5} />
              <div className="overline text-gold mt-6">Dental Practice Sales Experts</div>
              <h3 className="font-display text-3xl lg:text-4xl text-white mt-3">Confidential, valuation-led practice transitions.</h3>
              <p className="mt-6 text-white/75 max-w-md leading-relaxed">
                From solo GPs to multi-site DSOs, we advise on buy-side and sell-side dental transactions with full confidentiality.
              </p>
              <Link to="/medical-dental" className="btn-gold mt-auto w-fit" data-testid="dental-cta">Explore Dental</Link>
            </div>
          </div>
          <div className="relative overflow-hidden bg-navy-light min-h-[420px]">
            <img src="https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Medical clinic" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="relative p-10 lg:p-14 h-full flex flex-col">
              <HeartPulse className="w-8 h-8 text-gold" strokeWidth={1.5} />
              <div className="overline text-gold mt-6">Medical Clinic Brokerage</div>
              <h3 className="font-display text-3xl lg:text-4xl text-white mt-3">Multi-specialty clinic & MOB expertise.</h3>
              <p className="mt-6 text-white/75 max-w-md leading-relaxed">
                Medical office buildings, ambulatory centers, and specialty clinics — we navigate licensure, leases, and valuations.
              </p>
              <Link to="/medical-dental" className="btn-gold mt-auto w-fit" data-testid="medical-cta">Explore Medical</Link>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-10 lg:p-14">
            <HomeIcon className="w-8 h-8 text-gold" strokeWidth={1.5} />
            <div className="overline text-gold mt-6">Residential</div>
            <h3 className="font-display text-3xl text-navy mt-3">Trophy homes and generational estates.</h3>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Discreet, tailored representation for luxury buyers and sellers.
            </p>
            <Link to="/residential" className="btn-navy mt-8 w-fit" data-testid="residential-cta">Residential</Link>
          </div>
          <div className="bg-white border border-slate-200 p-10 lg:p-14">
            <Building2 className="w-8 h-8 text-gold" strokeWidth={1.5} />
            <div className="overline text-gold mt-6">Commercial</div>
            <h3 className="font-display text-3xl text-navy mt-3">Office, retail, and investment assets.</h3>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Institutional-grade advisory for leasing, investment, and disposition.
            </p>
            <Link to="/commercial" className="btn-navy mt-8 w-fit" data-testid="commercial-cta">Commercial</Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline text-gold">Clients</div>
          <h2 className="font-display text-3xl lg:text-4xl text-navy mt-3">What our principals say</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { q: "Forte handled the sale of my 3-op practice with absolute discretion and got us a premium multiple.", n: "Dr. Alisha Rao", r: "Seller · Dental Practice" },
              { q: "They found us the Class A Seattle tower we'd been hunting for two years. Institutional-caliber team.", n: "K. Hoshino", r: "Family Office Principal" },
              { q: "Sold our Malibu residence off-market at record price per square foot.", n: "James P.", r: "Residential Seller" },
            ].map((t) => (
              <figure key={t.n} className="bg-white p-10 border border-slate-200">
                <div className="text-gold font-display text-5xl leading-none">"</div>
                <blockquote className="mt-2 text-navy leading-relaxed">{t.q}</blockquote>
                <figcaption className="mt-6 text-sm">
                  <div className="font-semibold text-navy">{t.n}</div>
                  <div className="text-slate-500">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT SOLD */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-gold">Recently Sold & Leased</div>
            <h2 className="font-display text-3xl lg:text-4xl text-navy mt-3">Track record</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sold.map((l) => (
            <div key={l.id} className="relative">
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={l.images?.[0]} alt={l.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute top-3 left-3 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-semibold">{l.status}</div>
              </div>
              <div className="pt-4">
                <div className="overline text-gold">{l.location}</div>
                <div className="font-display text-lg text-navy mt-1">{l.title}</div>
                <div className="text-sm text-slate-500 mt-1">{formatPrice(l.price)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-navy text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="overline text-gold">Get in touch</div>
            <h2 className="font-display text-3xl lg:text-5xl mt-3">Have a property in mind,<br/>or one to bring to market?</h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              A senior advisor will respond within one business day. All conversations are confidential.
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <div><span className="overline text-gold block mb-1">Call</span><a href="tel:+14033366663" className="hover:text-gold transition" data-testid="home-contact-phone">+1 403-336-6663</a></div>
              <div><span className="overline text-gold block mb-1">Email</span><a href="mailto:rohin@forterealty.ca" className="hover:text-gold transition" data-testid="home-contact-email">rohin@forterealty.ca</a></div>
              <div><span className="overline text-gold block mb-1">Office</span>Calgary, Alberta · Serving all of Alberta</div>
            </div>
          </div>
          <form onSubmit={submitContact} className="bg-navy-light p-8 lg:p-10 space-y-5" data-testid="home-contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <input required placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-gold" data-testid="contact-name" />
              <input required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-gold" data-testid="contact-email" />
            </div>
            <input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-gold" data-testid="contact-phone" />
            <select value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} className="w-full bg-navy-light border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-gold" data-testid="contact-subject">
              <option>General Inquiry</option>
              <option>Residential</option>
              <option>Commercial</option>
              <option>Dental Practice</option>
              <option>Medical Clinic</option>
            </select>
            <textarea required rows="5" placeholder="Message" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-gold" data-testid="contact-message" />
            <button type="submit" className="btn-gold w-full" data-testid="contact-submit">Send Message</button>
            {contactMsg && <div className="text-gold text-sm" data-testid="contact-message-status">{contactMsg}</div>}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
